import { Injectable } from '@angular/core';
import { AuthenticationService } from '../http/authentication.service';

export interface ImpressaoTermicaCabecalhoOptions {
  incluirCabecalho?: boolean;
  titulo?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ImpressaoTermicaService {
  private readonly _larguraBobinaMm = 80;
  private readonly _margemHorizontalMm = 3;
  private readonly _larguraConteudoMm = this._larguraBobinaMm - (this._margemHorizontalMm * 2);

  constructor(
    private _authService: AuthenticationService
  ) { }

  public imprimirTexto(
    content: string | null | undefined,
    fallbackMessage: string,
    options?: ImpressaoTermicaCabecalhoOptions
  ): void {
    if (typeof window === 'undefined') {
      return;
    }

    const textoNormalizado = this.normalizarTexto(content, fallbackMessage);
    const textoComCabecalho = options?.incluirCabecalho
      ? this._adicionarCabecalho(textoNormalizado, options?.titulo)
      : textoNormalizado;
    const textoSeguro = this._escapeHtml(textoComCabecalho);

    const html = this._obterHtmlImpressao(textoSeguro);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const janela = window.open(url, '_blank', 'noopener,noreferrer,width=380,height=720');

    if (!janela) {
      URL.revokeObjectURL(url);
      return;
    }

    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  public normalizarTexto(content: string | null | undefined, fallbackMessage: string): string {
    const conteudoValido = content ?? fallbackMessage;

    const normalizado = conteudoValido
      .replace(/\\r\\n/g, '\n')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/^\"|\"$/g, '')
      .trim();

    return normalizado || fallbackMessage;
  }

  private _escapeHtml(content: string): string {
    return content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private _adicionarCabecalho(content: string, titulo?: string): string {
    const nomeEmpresa = this._obterNomeEmpresa();
    const nomeUsuario = this._obterNomeUsuario();
    const data = this._obterDataFormatada();
    const hora = this._obterHoraFormatada();
    const tituloImpressao = (titulo || '').trim().toUpperCase();

    const linhasCabecalho = [
      tituloImpressao,
      nomeEmpresa,
      `Data: ${data}`,
      `Hora: ${hora}`,
      `Usuario: ${nomeUsuario}`,
      '--------------------------------'
    ].filter(linha => linha.length > 0);

    return `${linhasCabecalho.join('\n')}\n${content}`;
  }

  private _obterNomeEmpresa(): string {
    const empresa = this._authService.user?.empresa;

    if (empresa && typeof empresa === 'object') {
      const nomeEmpresa = empresa.nomeFantasia || empresa.razaoSocial || empresa.nome;

      if (typeof nomeEmpresa === 'string' && nomeEmpresa.trim().length > 0) {
        return nomeEmpresa.trim();
      }
    }

    const databaseName = this._authService.databaseName;

    if (typeof databaseName === 'string' && databaseName.trim().length > 0) {
      return databaseName.trim();
    }

    return 'EMPRESA';
  }

  private _obterNomeUsuario(): string {
    const nomeUsuario = this._authService.user?.nome;

    if (typeof nomeUsuario === 'string' && nomeUsuario.trim().length > 0) {
      return nomeUsuario.trim();
    }

    return 'USUARIO';
  }

  private _obterDataFormatada(): string {
    const agora = new Date();

    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(agora);
  }

  private _obterHoraFormatada(): string {
    const agora = new Date();

    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(agora);
  }

  private _obterHtmlImpressao(content: string): string {
    return `
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <title></title>
          <style id="pageSizeStyle">
            @page {
              size: ${this._larguraBobinaMm}mm auto;
              margin: 0;
            }

            * {
              box-sizing: border-box;
            }

            html, body {
              width: ${this._larguraBobinaMm}mm;
              max-width: ${this._larguraBobinaMm}mm;
              margin: 0;
              padding: 0;
              background: #fff;
              color-adjust: exact;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            body {
              font-family: monospace;
              color: #000;
              padding: 2mm ${this._margemHorizontalMm}mm;
              display: flex;
              justify-content: center;
            }

            pre {
              margin: 0;
              width: ${this._larguraConteudoMm}mm;
              max-width: ${this._larguraConteudoMm}mm;
              white-space: pre-wrap;
              word-break: break-word;
              text-align: center;
              font-size: 14px;
              line-height: 1.25;
            }

            @media print {
              html, body {
                width: ${this._larguraBobinaMm}mm !important;
                max-width: ${this._larguraBobinaMm}mm !important;
                overflow: visible;
              }

              pre {
                width: ${this._larguraConteudoMm}mm !important;
                max-width: ${this._larguraConteudoMm}mm !important;
              }
            }
          </style>
        </head>
        <body>
          <pre id="textoRecibo">${content}</pre>
          <script>
            window.onload = function() {
              var textoRecibo = document.getElementById('textoRecibo');

              if (textoRecibo) {
                var minFontSizePx = 8;
                var maxFontSizePx = 14;
                var fontSizePx = maxFontSizePx;

                textoRecibo.style.fontSize = fontSizePx + 'px';

                while (
                  fontSizePx > minFontSizePx &&
                  textoRecibo.scrollWidth > textoRecibo.clientWidth
                ) {
                  fontSizePx -= 0.5;
                  textoRecibo.style.fontSize = fontSizePx + 'px';
                }
              }

              // Mantem a largura fixa em 80mm e calcula a altura da pagina pelo conteudo renderizado.
              var pxToMm = 25.4 / 96;
              var margemSegurancaPx = 8;
              var alturaConteudoPx = Math.ceil(document.body.scrollHeight + margemSegurancaPx);
              var alturaMinimaMm = 40;
              var alturaPaginaMm = Math.max(alturaMinimaMm, Math.ceil(alturaConteudoPx * pxToMm));
              var pageSizeStyle = document.getElementById('pageSizeStyle');

              if (pageSizeStyle) {
                pageSizeStyle.textContent = '@page { size: ${this._larguraBobinaMm}mm ' + alturaPaginaMm + 'mm; margin: 0; }';
              }

              window.focus();
              setTimeout(function() {
                window.print();
              }, 150);
            }
          </script>
        </body>
      </html>
    `;
  }
}
